import { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { CarrierWithRelations, Company } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import EditCarrierModal from "@/components/modals/edit-carrier-modal";
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import NotesSection from "@/components/notes/notes-section";
import { formatPhoneNumber } from "@/lib/utils";
import ContactsSection from "@/components/contacts/contacts-section";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Truck, Mail, Phone, MapPin, Building2, Edit, FileText, CheckCircle, ExternalLink, Search, Calendar, Plus, AlertCircle } from "lucide-react";
import { useLocation } from "wouter";
import { format } from "date-fns";

interface CarrierDetailsProps {
  open: boolean;
  onClose: () => void;
  carrierId: number;
}

export default function CarrierDetails({ open, onClose, carrierId }: CarrierDetailsProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateWorkOrderModal, setShowCreateWorkOrderModal] = useState(false);
  const [workOrderSearchTerm, setWorkOrderSearchTerm] = useState("");
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState("all");
  
  const { data: carrier, isLoading } = useQuery<CarrierWithRelations>({
    queryKey: ["/api/carriers", carrierId],
    enabled: open && !!carrierId,
    retry: false,
  });

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
    retry: false,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  // Fetch company contacts to find primary contact (carrier belongs to a company)
  const companyId = carrier?.company?.id;
  const { data: companyContacts = [], isLoading: isLoadingContacts } = useQuery<any[]>({
    queryKey: ["/api/companies", companyId, "contacts"],
    queryFn: async () => {
      const response = await fetch(`/api/companies/${companyId}/contacts`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch contacts");
      return response.json();
    },
    enabled: open && !!companyId,
  });

  // Fetch users for the create work order modal
  const { data: users = [] } = useQuery<any[]>({
    queryKey: ["/api/assignable-users"],
    enabled: open && !!carrierId,
  });

  // Find the primary contact for the carrier's company (uses isPrimary boolean only)
  const primaryContact = useMemo(() => {
    const primaryRelation = companyContacts.find((cc: any) => cc.isPrimary === true);
    return primaryRelation?.contact;
  }, [companyContacts]);

  const updateCarrierMutation = useMutation({
    mutationFn: async (carrierData: any) => {
      await apiRequest("PUT", `/api/carriers/${carrierId}`, carrierData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/carriers", carrierId] });
      toast({
        title: "Success",
        description: "Carrier updated successfully",
      });
      setShowEditModal(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update carrier",
        variant: "destructive",
      });
    },
  });

  const handleUpdateCarrier = (carrierData: any) => {
    updateCarrierMutation.mutate(carrierData);
  };

  // Create work order mutation
  const createWorkOrderMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/work-orders", data);
      return await response.json();
    },
    onSuccess: (createdWorkOrder) => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers", carrierId] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setShowCreateWorkOrderModal(false);
      // Navigate to the newly created work order details page
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  const filteredWorkOrders = useMemo(() => {
    if (!carrier?.workOrders) return [];
    
    return carrier.workOrders.filter((workOrder: any) => {
      const matchesSearch = workOrderSearchTerm === "" || 
        workOrder.workOrderNumber?.toLowerCase().includes(workOrderSearchTerm.toLowerCase()) ||
        workOrder.serviceType?.toLowerCase().includes(workOrderSearchTerm.toLowerCase());
      
      const matchesStatus = workOrderStatusFilter === "all" || 
        (workOrderStatusFilter === "active" && workOrder.status !== "closed" && workOrder.status !== "cancelled") ||
        workOrder.status === workOrderStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [carrier?.workOrders, workOrderSearchTerm, workOrderStatusFilter]);

  const handleViewProfile = (docketPrefix: string, docketNumber: string) => {
    if (docketNumber) {
      const profileUrl = `https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=MC_MX&query_string=${docketNumber}`;
      window.open(profileUrl, "_blank");
    }
  };

  const getFMCSAStatusColor = (status: string | null | undefined) => {
    if (!status) return "bg-gray-100 text-gray-800";
    const upperStatus = status.toUpperCase();
    switch (upperStatus) {
      case "ACTIVE":
      case "A":
      case "AUTHORIZED":
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "P":
        return "bg-yellow-100 text-yellow-800";
      case "SUSPENDED":
      case "S":
        return "bg-red-100 text-red-800";
      case "REVOKED":
      case "R":
      case "INACTIVE":
      case "NONE":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatFMCSAStatus = (status: string | null | undefined) => {
    if (!status) return "N/A";
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl">
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Loading carrier details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!carrier) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl">
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">Carrier not found</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent 
          className="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // Prevent Dialog from closing when Create Work Order modal is open
            if (showCreateWorkOrderModal || showEditModal) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Prevent Dialog from closing when Create Work Order modal is open
            if (showCreateWorkOrderModal || showEditModal) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            // Prevent Dialog from closing on Escape when child modal is open
            if (showCreateWorkOrderModal || showEditModal) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                {carrier.name}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Badge className={getFMCSAStatusColor(carrier.commonAuthorityStatus)}>
                  {formatFMCSAStatus(carrier.commonAuthorityStatus)}
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="authority">Authority</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="work-orders">Work Orders</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Carrier Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{carrier.name}</p>
                        <p className="text-sm text-gray-500">Carrier Name</p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{carrier.email || "Not provided"}</p>
                        <p className="text-sm text-gray-500">Email</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{formatPhoneNumber(carrier.phone) || "Not provided"}</p>
                        <p className="text-sm text-gray-500">Phone</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div>
                        <p className="font-medium">{carrier.address || "Not provided"}</p>
                        <p className="text-sm text-gray-500">Address</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">FMCSA Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{carrier.company?.dotProfile?.dotNumber || "Not set"}</p>
                        <p className="text-sm text-gray-500">DOT Number (Company)</p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <p className="font-medium">{carrier.docketNumber ? `${carrier.docketPrefix}-${carrier.docketNumber}` : "Not set"}</p>
                        <p className="text-sm text-gray-500">Docket Number</p>
                      </div>
                      {carrier.docketNumber && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewProfile(carrier.docketPrefix, carrier.docketNumber || "")}
                          className="ml-auto"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Profile
                        </Button>
                      )}
                    </div>
                    
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Company Association</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{carrier.company?.name || "Not associated"}</p>
                      <p className="text-sm text-gray-500">Company</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="authority" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Authority Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Common Authority:</span>
                        <Badge className={getFMCSAStatusColor(carrier.commonAuthorityStatus)}>
                          {formatFMCSAStatus(carrier.commonAuthorityStatus)}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Contract Authority:</span>
                        <Badge className={getFMCSAStatusColor(carrier.contractAuthorityStatus)}>
                          {formatFMCSAStatus(carrier.contractAuthorityStatus)}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Broker Authority:</span>
                        <Badge className={getFMCSAStatusColor(carrier.brokerAuthorityStatus)}>
                          {formatFMCSAStatus(carrier.brokerAuthorityStatus)}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Pending Applications</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Common Application Pending:</span>
                        <Badge className={carrier.commonApplicationPending ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"}>
                          {carrier.commonApplicationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Contract Application Pending:</span>
                        <Badge className={carrier.contractApplicationPending ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"}>
                          {carrier.contractApplicationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Broker Application Pending:</span>
                        <Badge className={carrier.brokerApplicationPending ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"}>
                          {carrier.brokerApplicationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Common Revocation Pending:</span>
                        <Badge className={carrier.commonRevocationPending ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"}>
                          {carrier.commonRevocationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Contract Revocation Pending:</span>
                        <Badge className={carrier.contractRevocationPending ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"}>
                          {carrier.contractRevocationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Broker Revocation Pending:</span>
                        <Badge className={carrier.brokerRevocationPending ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"}>
                          {carrier.brokerRevocationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Operating Authority</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Property Carriage:</span>
                      <Badge className={carrier.propertyCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}>
                        {carrier.propertyCarriage ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Passenger Carriage:</span>
                      <Badge className={carrier.passengerCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}>
                        {carrier.passengerCarriage ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Household Goods Carriage:</span>
                      <Badge className={carrier.householdGoodsCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}>
                        {carrier.householdGoodsCarriage ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Private Carriage:</span>
                      <Badge className={carrier.privateCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}>
                        {carrier.privateCarriage ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Enterprise Carriage:</span>
                      <Badge className={carrier.enterpriseCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}>
                        {carrier.enterpriseCarriage ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Insurance Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">BIPD Required:</span>
                      <span className="font-medium">{carrier.bipdRequired || "N/A"}</span>
                      <span className="text-gray-600">BIPD On File:</span>
                      <span className="font-medium">{carrier.bipdOnFile || "N/A"}</span>
                    </div>
                    <Separator />
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">Cargo Required:</span>
                      <span className="font-medium">{carrier.cargoRequired || "N/A"}</span>
                      <span className="text-gray-600">Cargo On File:</span>
                      <span className="font-medium">{carrier.cargoOnFile || "N/A"}</span>
                    </div>
                    <Separator />
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">Bond Required:</span>
                      <span className="font-medium">{carrier.bondRequired || "N/A"}</span>
                      <span className="text-gray-600">Bond On File:</span>
                      <span className="font-medium">{carrier.bondOnFile || "N/A"}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Undeliverable Mail:</span>
                      <Badge className={carrier.undeliverableMail ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"}>
                        {carrier.undeliverableMail ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="contacts">
              {carrier.company?.id ? (
                <ContactsSection
                  entityType="company"
                  entityId={carrier.company.id.toString()}
                  title="Carrier Contacts"
                />
              ) : (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-gray-500">No company associated with this carrier</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="work-orders">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Work Orders</CardTitle>
                    <Button 
                      size="sm" 
                      onClick={() => setShowCreateWorkOrderModal(true)}
                      data-testid="button-create-work-order"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Work Order
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {!primaryContact && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          No primary contact is set for this carrier's company. Work orders require a requesting contact. 
                          Please set a primary contact in the Contacts tab first.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search work orders..."
                          value={workOrderSearchTerm}
                          onChange={(e) => setWorkOrderSearchTerm(e.target.value)}
                          className="pl-10"
                          data-testid="input-search-work-orders"
                        />
                      </div>
                      <Select value={workOrderStatusFilter} onValueChange={setWorkOrderStatusFilter}>
                        <SelectTrigger className="w-48" data-testid="select-status-filter">
                          <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="active">Active Only</SelectItem>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                          <SelectItem value="validating">Validating</SelectItem>
                          <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                          <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                          <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                          <SelectItem value="work_complete">Work Complete</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {filteredWorkOrders.length > 0 ? (
                      <div className="space-y-2">
                        {filteredWorkOrders.map((workOrder: any) => (
                          <div key={workOrder.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <button
                                  onClick={() => {
                                    onClose();
                                    setLocation(`/work-orders/${workOrder.id}`);
                                  }}
                                  className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                                  data-testid={`link-work-order-${workOrder.id}`}
                                >
                                  {workOrder.workOrderNumber}
                                </button>
                                <p className="text-sm text-gray-500 mt-1">
                                  {workOrder.serviceType?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>Created: {workOrder.createdAt ? format(new Date(workOrder.createdAt), 'MMM d, yyyy') : 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>Updated: {workOrder.updatedAt ? format(new Date(workOrder.updatedAt), 'MMM d, yyyy') : 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                              <Badge className={workOrder.status === "completed" ? "bg-green-100 text-green-800" : workOrder.status === "cancelled" ? "bg-gray-100 text-gray-800" : "bg-yellow-100 text-yellow-800"}>
                                {workOrder.status?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        {carrier.workOrders && carrier.workOrders.length > 0 ? "No work orders match your search" : "No work orders found"}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="notes">
              <NotesSection
                entityType="carrier"
                entityId={carrierId.toString()}
                title="Carrier Notes"
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      
      {showEditModal && (
        <EditCarrierModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateCarrier}
          isLoading={updateCarrierMutation.isPending}
          carrier={carrier}
          companies={companies || []}
        />
      )}

      {showCreateWorkOrderModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateWorkOrderModal(false)}
          onSubmit={(data) => createWorkOrderMutation.mutate(data)}
          isLoading={createWorkOrderMutation.isPending}
          users={users}
          currentUserId={currentUser?.id}
          prePopulate={
            primaryContact && carrier?.company?.id
              ? { 
                  contactId: primaryContact.id,
                  contactName: `${primaryContact.firstName} ${primaryContact.lastName}`,
                  companyId: carrier.company.id,
                  carrierId: carrierId,
                }
              : undefined
          }
        />
      )}
    </>
  );
}